'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Link2, Mail, MoreHorizontal, X } from 'lucide-react';

/**
 * "Share to" sheet: a row of round targets (copy link, WhatsApp, Telegram, X,
 * Facebook, email, and the device's own share menu when it has one). Centered
 * on desktop, a bottom sheet on phones. Portals to <body> above the clip viewer.
 */

export type ShareMethod = 'copy' | 'whatsapp' | 'telegram' | 'x' | 'facebook' | 'email' | 'native';

type Target = {
  key: ShareMethod;
  label: string;
  bg: string;
  icon: React.ReactNode;
  href: (url: string, text: string) => string;
  sameTab?: boolean;
};

const svg = (children: React.ReactNode, filled = true) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="size-7"
    fill={filled ? 'currentColor' : 'none'}
    stroke={filled ? 'none' : 'currentColor'}
    strokeWidth={1.8}
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const WhatsAppIcon = svg(
  <>
    <path d="M3.6 20.4l1.2-4.1a8.4 8.4 0 1 1 3.1 3Z" />
    <path
      fill="currentColor"
      stroke="none"
      d="M9.2 7.7c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-.9.9-.9 2.2s.9 2.5 1 2.7c.1.2 1.8 2.9 4.5 3.9 2.2.9 2.7.7 3.2.7.5-.1 1.6-.7 1.8-1.3.2-.6.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3l-1.8-.9c-.3-.1-.5-.1-.6.1l-.8 1c-.2.2-.3.2-.6.1-.3-.1-1.1-.4-2.1-1.3-.8-.7-1.3-1.5-1.4-1.8-.2-.3 0-.4.1-.5l.4-.5.3-.5c.1-.2 0-.4 0-.5Z"
    />
  </>,
  false,
);

const TelegramIcon = svg(
  <path d="M20.7 4.3 2.9 11.2c-1 .4-1 1.6.1 1.9l4.4 1.4 1.7 5.3c.2.7 1.1.9 1.6.4l2.5-2.4 4.7 3.5c.6.4 1.4.1 1.6-.6l3-14.5c.2-.9-.7-1.6-1.6-1.3ZM17.2 8.2l-7.7 7-.3 3-1.2-4 8.9-6.3c.3-.2.6.1.3.3Z" />,
);

const XIcon = svg(
  <path d="M18.2 2H21l-6.4 7.3L22.3 22h-6l-4.7-6.2L6.2 22H3.4l6.9-7.9L2 2h6.1l4.3 5.7L18.2 2Zm-1 18.3h1.6L7.9 3.6H6.2l11 16.7Z" />,
);

const FacebookIcon = svg(
  <path d="M15.1 22V13.8h2.8l.4-3.2h-3.2V8.5c0-.9.3-1.6 1.6-1.6h1.7V4.1c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.4H8.9v3.2h2.8V22Z" />,
);

const TARGETS: Target[] = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    bg: '#25D366',
    icon: WhatsAppIcon,
    href: (url, text) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    key: 'telegram',
    label: 'Telegram',
    bg: '#2AABEE',
    icon: TelegramIcon,
    href: (url, text) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    key: 'x',
    label: 'X',
    bg: '#000000',
    icon: XIcon,
    href: (url, text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    key: 'facebook',
    label: 'Facebook',
    bg: '#1877F2',
    icon: FacebookIcon,
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    key: 'email',
    label: 'Email',
    bg: 'rgba(255,255,255,0.12)',
    icon: <Mail className="size-7" strokeWidth={1.8} />,
    href: (url, text) =>
      `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
    sameTab: true,
  },
];

const circle =
  'flex size-14 items-center justify-center rounded-full text-white transition-transform group-hover:scale-105 group-active:scale-95 sm:size-16';
const item =
  'group flex w-16 shrink-0 cursor-pointer flex-col items-center gap-2 text-xs text-white/85 outline-none sm:w-[72px] sm:text-sm';

export function ShareSheet({
  open,
  onClose,
  url,
  text,
  title = 'Share to',
  onShare,
}: {
  open: boolean;
  onClose: () => void;
  url: string;
  /** Message that goes with the link (WhatsApp, X, email...). */
  text: string;
  title?: string;
  /** Analytics hook, called once per target used. */
  onShare?: (method: ShareMethod) => void;
}) {
  const [mounted, setMounted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canNative, setCanNative] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCanNative(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Don't also close the clip viewer underneath.
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!mounted || !open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      onShare?.('copy');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: leave the sheet open so another target can be used.
    }
  };

  const native = async () => {
    try {
      await navigator.share({ title: text, text, url });
      onShare?.('native');
      onClose();
    } catch {
      // Dismissed.
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60 sm:items-center"
      onClick={onClose}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-sheet-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[680px] rounded-t-3xl border border-white/10 bg-[#1f1f1f] pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4 text-white shadow-2xl sm:rounded-3xl sm:pb-6"
      >
        <div className="relative flex items-center justify-center px-12 pb-5">
          <h2 id="share-sheet-title" className="text-base font-semibold sm:text-lg">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-1/2 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex gap-3 overflow-x-auto px-5 pb-1 [scrollbar-width:none] sm:gap-4 sm:px-6 [&::-webkit-scrollbar]:hidden">
          <button type="button" onClick={copy} className={item}>
            <span className={circle} style={{ backgroundColor: '#3B6CF6' }}>
              {copied ? <Check className="size-7" strokeWidth={2.2} /> : <Link2 className="size-7" strokeWidth={2.2} />}
            </span>
            <span aria-live="polite">{copied ? 'Copied' : 'Copy link'}</span>
          </button>

          {TARGETS.map((t) => (
            <a
              key={t.key}
              href={t.href(url, text)}
              target={t.sameTab ? undefined : '_blank'}
              rel="noopener noreferrer"
              onClick={() => onShare?.(t.key)}
              className={item}
            >
              <span
                className={`${circle} ${t.key === 'x' ? 'ring-1 ring-white/20' : ''}`}
                style={{ backgroundColor: t.bg }}
              >
                {t.icon}
              </span>
              <span>{t.label}</span>
            </a>
          ))}

          {canNative && (
            <button type="button" onClick={native} className={item}>
              <span className={circle} style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                <MoreHorizontal className="size-7" strokeWidth={2} />
              </span>
              <span>More</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
